import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { capturePhoto } from '@/services/cameraService';

export interface FileUploadManagerOptions {
  maxSizeMB: number;
  allowedFileTypes: string[];
  onFilesChange?: (files: File[]) => void;
}

export function useFileUploadManager(options: FileUploadManagerOptions) {
  const { maxSizeMB, allowedFileTypes, onFilesChange } = options;
  const [files, setFiles] = useState<File[]>([]);
  const [viewingImages, setViewingImages] = useState<string[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  const handleFileSelection = useCallback((fileList: FileList | File[]) => {
    const validFiles: File[] = [];
    const filesArray = Array.from(fileList);

    for (const file of filesArray) {
      if (file.size > maxSizeBytes) {
        toast.error(`${file.name} exceeds maximum size of ${maxSizeMB}MB`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      const newFiles = [...files, ...validFiles];
      setFiles(newFiles);
      onFilesChange?.(newFiles);
      toast.success(`Added ${validFiles.length} file${validFiles.length > 1 ? 's' : ''}`);
    }
  }, [files, maxSizeBytes, maxSizeMB, onFilesChange]);

  const handleCameraCapture = useCallback(async () => {
    try {
      const photoFile = await capturePhoto(90);
      if (photoFile) {
        if (photoFile.size > maxSizeBytes) {
          toast.error(`Photo exceeds maximum size of ${maxSizeMB}MB`);
          return;
        }

        const newFiles = [...files, photoFile];
        setFiles(newFiles);
        onFilesChange?.(newFiles);
        toast.success('Photo captured successfully');
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast.error('Failed to capture photo');
    }
  }, [files, maxSizeBytes, maxSizeMB, onFilesChange]);

  const handleFileUpload = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = allowedFileTypes.join(',');
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        handleFileSelection(target.files);
      }
    };
    input.click();
  }, [allowedFileTypes, handleFileSelection]);

  const handleRemoveFile = useCallback((index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    onFilesChange?.(newFiles);
    toast.success('File removed');
  }, [files, onFilesChange]);

  const openImageViewer = useCallback((index: number) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    const imageUrls = imageFiles.map(f => URL.createObjectURL(f));
    setViewingImages(imageUrls);
    setCurrentImageIndex(index);
    setImageViewerOpen(true);
  }, [files]);

  const closeImageViewer = useCallback(() => {
    setImageViewerOpen(false);
    // Clean up object URLs
    viewingImages.forEach(url => URL.revokeObjectURL(url));
    setViewingImages([]);
    setCurrentImageIndex(0);
  }, [viewingImages]);

  const resetFiles = useCallback(() => {
    setFiles([]);
    onFilesChange?.([]);
  }, [onFilesChange]);

  const setFilesList = useCallback((newFiles: File[]) => {
    setFiles(newFiles);
    onFilesChange?.(newFiles);
  }, [onFilesChange]);

  return {
    files,
    setFiles: setFilesList,
    resetFiles,
    handleFileSelection,
    handleCameraCapture,
    handleFileUpload,
    handleRemoveFile,
    openImageViewer,
    closeImageViewer,
    imageViewerState: {
      isOpen: imageViewerOpen,
      images: viewingImages,
      currentIndex: currentImageIndex,
      setCurrentIndex: setCurrentImageIndex,
    },
  };
}
